import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { isEqual } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useClient } from 'urql';

import {
  COMPOSITION_GET_DATA_V3,
  type CompositionV3DataQuery,
  type CompositionV3DataQueryVariables,
  Dimension1,
  type GetSentimentDataQuery,
  type GetSentimentDataQueryVariables,
  type GetSentimentSnapshotQuery,
  SENTIMENT_GET_OVERTIME_DATA_V2,
  SENTIMENT_SNAPSHOT,
} from '@revelio/data-access';

import { useCustomSkillTaxonomyTree } from '../../../../shared/selection-lists/hooks/useCustomSkillTaxonomyTree';
import { normalizeBarScoresData } from '../../../sentiment/sentimentV2/hooks/use-get-sentiment-reviews-data';
import { transformToSingleEntity } from '../../../sentiment/sentimentV2/utils/transform-to-single-entity';
import {
  useFilterContext,
  useIsTaxonomyResolved,
} from '../../filters/contexts/FilterProvider';
import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import { usePrimaryFilterState } from '../../filters/hooks/usePrimaryFilterState';
import type { TreeSelection } from '../../filters/types/filters';
import {
  FilterId,
  FilterType,
  type PrimaryFilterId,
  type TreeFilter,
} from '../../filters/types/filters';
import { getCompositionV3Variables } from '../../filters/utils/composition-v3/getCompositionV3Variables';
import { getDim1 } from '../../filters/utils/getDim1';
import { getSentimentV2Filters } from '../../filters/utils/getSentimentV2Filters';
import { useChart } from '../Chart/ChartProvider';
import { isCountry, isRegion } from '../Chart/Map/utils/location-type-utils';
import { MAX_REPORT_ENTITIES } from '../PageConfigProvider/constants';
import type { SentimentChartType } from '../PageConfigProvider/types/chart';
import type { SentimentMetricValues } from '../PageConfigProvider/types/metric';
import { usePageState } from '../PageState/PageStateProvider';
import { isCompositionV3FullMetrics } from './composition/composition-v3/composition-v3-graphql-response';
import {
  type BreakdownSingleDataSet,
  type DataProviderChildren,
  DataProviderQueryStatus,
  DataType,
  type SingleDataSet,
  type TimeseriesDataSet,
} from './types';
import { convertSentimentData } from './utils/convert-sentiment-data';
import { useUrqlQueryWithStatus } from './utils/data-provider-results-utils';

export type SentimentData =
  | SingleDataSet
  | TimeseriesDataSet
  | BreakdownSingleDataSet;

type SentimentEntity = NonNullable<
  NonNullable<GetSentimentDataQuery['sentiment']>[number]
>;

type SentimentScorePoint = {
  value?: number | null;
  weight?: number | null;
};

const getRoundedCount = (weight: number | null | undefined): number => {
  if (typeof weight !== 'number' || Number(weight) === 0) return 0;
  return Math.max(1, Math.round(weight));
};

const getSelectionIdNumber = (selectionId: string): number | null => {
  const lastToken = selectionId.split('_').slice(-1)[0];
  const parsed = Number(lastToken);
  return Number.isFinite(parsed) ? parsed : null;
};

type CompanySelection = {
  id: number;
  name: string;
  levelId: string;
};

const buildCompanySentimentFilterPatch = (
  company: CompanySelection
): Pick<
  NonNullable<GetSentimentDataQueryVariables['filters']>,
  'company' | 'rics_k10' | 'rics_k50' | 'rics_k400'
> => {
  switch (company.levelId) {
    case 'rics_k10':
      return { rics_k10: [company.id] };
    case 'rics_k50':
      return { rics_k50: [company.id] };
    case 'rics_k400':
      return { rics_k400: [company.id] };
    // biome-ignore lint/complexity/noUselessSwitchCase: migrated from develop
    case 'company':
    default:
      return { company: [company.id] };
  }
};

const mergeCompanySentimentFilters = (
  companies: CompanySelection[]
): Pick<
  NonNullable<GetSentimentDataQueryVariables['filters']>,
  'company' | 'rics_k10' | 'rics_k50' | 'rics_k400'
> => {
  const merged: {
    company?: number[];
    rics_k10?: number[];
    rics_k50?: number[];
    rics_k400?: number[];
  } = {};

  companies.forEach((company) => {
    const patch = buildCompanySentimentFilterPatch(company);
    if (patch.company) {
      const numbers = patch.company.filter(
        (v): v is number => typeof v === 'number' && Number.isFinite(v)
      );
      merged.company = [...(merged.company ?? []), ...numbers];
    }
    if (patch.rics_k10) {
      const numbers = patch.rics_k10.filter(
        (v): v is number => typeof v === 'number' && Number.isFinite(v)
      );
      merged.rics_k10 = [...(merged.rics_k10 ?? []), ...numbers];
    }
    if (patch.rics_k50) {
      const numbers = patch.rics_k50.filter(
        (v): v is number => typeof v === 'number' && Number.isFinite(v)
      );
      merged.rics_k50 = [...(merged.rics_k50 ?? []), ...numbers];
    }
    if (patch.rics_k400) {
      const numbers = patch.rics_k400.filter(
        (v): v is number => typeof v === 'number' && Number.isFinite(v)
      );
      merged.rics_k400 = [...(merged.rics_k400 ?? []), ...numbers];
    }
  });

  return merged;
};

const stripCompanySentimentFilters = (
  filters: GetSentimentDataQueryVariables['filters']
): GetSentimentDataQueryVariables['filters'] => {
  if (!filters) return filters;
  const { company, rics_k10, rics_k50, rics_k400, ...rest } = filters;
  return rest;
};

type SecondaryBreakdownDimension = 'role' | 'geography';

const getSecondaryBreakdownDimension = (
  chartSecondaryDimension?: string
): SecondaryBreakdownDimension | null => {
  const normalized = chartSecondaryDimension?.toLowerCase();
  if (normalized === 'role') return 'role';
  if (normalized === 'geography') return 'geography';
  return null;
};

const getSentimentSnapshot = (
  entity: SentimentEntity,
  metric: SentimentMetricValues
): { value: number; count: number } => {
  const scores = entity?.result?.scores as
    | Record<string, SentimentScorePoint[] | null>
    | null
    | undefined;

  const series = scores?.[metric] ?? undefined;
  const last =
    Array.isArray(series) && series.length > 0
      ? series[series.length - 1]
      : undefined;

  const value = typeof last?.value === 'number' ? last.value / 100 : 0;
  const count = getRoundedCount(last?.weight);
  return { value, count };
};

const buildGeographySentimentFilterPatch = (
  geoId: number,
  label: string
): Pick<
  NonNullable<GetSentimentDataQueryVariables['filters']>,
  'region' | 'country' | 'msa'
> => {
  const normalized = label.toLowerCase().trim();

  if (isRegion(normalized)) {
    return { region: [geoId] };
  }
  if (isCountry(normalized)) {
    return { country: [geoId] };
  }
  return { msa: [geoId] };
};

const buildGeographySentimentFiltersFromDims = (
  dims: Array<{ id: number; label: string }>,
  maxFilters = MAX_REPORT_ENTITIES
): Pick<
  NonNullable<GetSentimentDataQueryVariables['filters']>,
  'region' | 'country' | 'msa'
> => {
  const region = new Set<number>();
  const country = new Set<number>();
  const msa = new Set<number>();

  dims.slice(0, maxFilters).forEach((d) => {
    const patch = buildGeographySentimentFilterPatch(d.id, d.label);
    patch.region?.forEach((raw) => {
      if (typeof raw === 'number' && Number.isFinite(raw)) region.add(raw);
      if (typeof raw === 'string') {
        const n = Number(raw);
        if (Number.isFinite(n)) region.add(n);
      }
    });
    patch.country?.forEach((raw) => {
      if (typeof raw === 'number' && Number.isFinite(raw)) country.add(raw);
      if (typeof raw === 'string') {
        const n = Number(raw);
        if (Number.isFinite(n)) country.add(n);
      }
    });
    patch.msa?.forEach((raw) => {
      if (typeof raw === 'number' && Number.isFinite(raw)) msa.add(raw);
      if (typeof raw === 'string') {
        const n = Number(raw);
        if (Number.isFinite(n)) msa.add(n);
      }
    });
  });

  return {
    ...(region.size ? { region: Array.from(region).slice(0, maxFilters) } : {}),
    ...(country.size
      ? { country: Array.from(country).slice(0, maxFilters) }
      : {}),
    ...(msa.size ? { msa: Array.from(msa).slice(0, maxFilters) } : {}),
  };
};

const buildRoleSentimentFiltersFromDims = (
  dims: Array<{ id: number; label: string }>,
  baseCustomRole: NonNullable<
    NonNullable<GetSentimentDataQueryVariables['filters']>['custom_role']
  >,
  maxFilters = MAX_REPORT_ENTITIES
): Pick<
  NonNullable<GetSentimentDataQueryVariables['filters']>,
  'custom_role'
> => {
  return {
    custom_role: {
      ...baseCustomRole,
      entities: dims.slice(0, maxFilters).map((d) => ({
        id: String(d.id),
        levelId: 'role_k7',
      })),
    },
  };
};

const getCompositionBreakdownDims = (
  composition: CompositionV3DataQuery['composition_v3'] | null | undefined,
  dimension: SecondaryBreakdownDimension,
  baselineCompanyId: number,
  limit: number
): Array<{ id: number; label: string }> => {
  const entities = composition?.entities ?? [];
  const baseline =
    entities.find((e) => e.id === baselineCompanyId) ?? entities[0];
  if (!baseline) return [];

  const metrics = baseline.metrics;
  if (!isCompositionV3FullMetrics(metrics)) return [];

  const breakdown = dimension === 'role' ? metrics.role : metrics.geography;
  if (!breakdown || !Array.isArray(breakdown)) return [];

  const scored = breakdown
    .map((b) => {
      const id = b.id ?? 0;
      const label = b.short_name ?? b.long_name ?? String(id);
      const headcountSeries = Array.isArray(b.headcount) ? b.headcount : [];
      const last = headcountSeries[headcountSeries.length - 1];
      const count = last?.count ?? 0;
      if (!id || count <= 0) return null;
      return { id, label, count };
    })
    .filter((x): x is NonNullable<typeof x> => x !== null)
    .sort((a, b) => b.count - a.count)
    .slice(0, limit)
    .map(({ id, label }) => ({ id, label }));

  return scored;
};

const EMPTY_BREAKDOWN_SINGLE: BreakdownSingleDataSet = {
  type: DataType.BREAKDOWN_SINGLE,
  data: [],
};

const SentimentRoleGeoBreakdownProvider = ({
  metric,
  isSnapshot,
  secondaryBreakdown,
  sentimentVariables,
  primaryFilterSelections,
  children,
}: {
  metric: SentimentMetricValues;
  isSnapshot: boolean;
  secondaryBreakdown: SecondaryBreakdownDimension;
  sentimentVariables: GetSentimentDataQueryVariables;
  primaryFilterSelections: TreeSelection[] | undefined;
  children: DataProviderChildren<SentimentData | undefined>;
}) => {
  const gqlClient = useClient();
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  const { activeFilterMap, geographyMode } = useFilterContext();
  const { customSkillTree } = useCustomSkillTaxonomyTree();

  const companySelectionsFromEffectiveFilters = useMemo(() => {
    const companyFilter = effectiveFilters.find(
      (f): f is TreeFilter =>
        f.id === FilterId.COMPANY && f.type === FilterType.TREE
    );
    const competitorsFilter = effectiveFilters.find(
      (f): f is TreeFilter =>
        f.id === FilterId.COMPETITORS && f.type === FilterType.TREE
    );

    return [
      ...(companyFilter?.selections ?? []),
      ...(competitorsFilter?.selections ?? []),
    ];
  }, [effectiveFilters]);

  const allCompaniesUncapped = useMemo(() => {
    const byKey = new Map<string, CompanySelection>();
    companySelectionsFromEffectiveFilters.forEach((s) => {
      const id = getSelectionIdNumber(s.id);
      if (id === null) return;
      const name = s.name ?? String(id);
      const key = `${s.levelId}:${id}`;
      if (!byKey.has(key)) {
        byKey.set(key, { id, name, levelId: s.levelId });
      }
    });
    return Array.from(byKey.values());
  }, [companySelectionsFromEffectiveFilters]);

  const baselineCompanyId = useMemo(() => {
    const primaryFirst = primaryFilterSelections?.[0];
    const primaryId =
      primaryFirst && primaryFirst.name
        ? getSelectionIdNumber(primaryFirst.id)
        : null;
    if (primaryId !== null) return primaryId;
    return allCompaniesUncapped[0]?.id ?? null;
  }, [allCompaniesUncapped, primaryFilterSelections]);

  const allCompanies = useMemo(() => {
    if (allCompaniesUncapped.length <= MAX_REPORT_ENTITIES) {
      return allCompaniesUncapped;
    }

    if (baselineCompanyId === null) {
      return allCompaniesUncapped.slice(0, MAX_REPORT_ENTITIES);
    }

    const baseline =
      allCompaniesUncapped.find((c) => c.id === baselineCompanyId) ?? null;
    const rest = allCompaniesUncapped.filter((c) => c.id !== baselineCompanyId);

    return baseline
      ? [baseline, ...rest].slice(0, MAX_REPORT_ENTITIES)
      : allCompaniesUncapped.slice(0, MAX_REPORT_ENTITIES);
  }, [allCompaniesUncapped, baselineCompanyId]);

  const compositionVariables: CompositionV3DataQueryVariables | null =
    useMemo(() => {
      if (baselineCompanyId === null) return null;

      const base = getCompositionV3Variables({
        primaryDimension: FilterId.COMPANY,
        effectiveFilters,
        isSnapshot,
        customSkillTree,
        activeFilterMap,
        geographyMode,
      });

      if (!base?.filters?.start_date || !base?.filters?.end_date) return null;
      const entitiesArray = Array.isArray(base.entities)
        ? base.entities
        : [base.entities];
      if (entitiesArray.length === 0) return null;

      return {
        ...base,
        entities: entitiesArray.slice(0, MAX_REPORT_ENTITIES),
      };
    }, [
      activeFilterMap,
      baselineCompanyId,
      customSkillTree,
      effectiveFilters,
      geographyMode,
      isSnapshot,
    ]);

  const [fetching, setFetching] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [dataSet, setDataSet] = useState<BreakdownSingleDataSet>(
    EMPTY_BREAKDOWN_SINGLE
  );

  const lastRequestRef = useRef<{
    compositionVariables: CompositionV3DataQueryVariables | null;
    sentimentFilters: GetSentimentDataQueryVariables['filters'];
    metric: SentimentMetricValues;
    secondaryBreakdown: SecondaryBreakdownDimension;
    baselineCompanyId: number;
    allCompanies: CompanySelection[];
  } | null>(null);

  useEffect(() => {
    (async () => {
      if (baselineCompanyId === null || compositionVariables === null) {
        setHasError(false);
        setFetching(false);
        setDataSet(EMPTY_BREAKDOWN_SINGLE);
        return;
      }

      if (allCompanies.length < 2) {
        setHasError(false);
        setFetching(false);
        setDataSet(EMPTY_BREAKDOWN_SINGLE);
        return;
      }

      const request = {
        compositionVariables,
        sentimentFilters: sentimentVariables.filters,
        metric,
        secondaryBreakdown,
        baselineCompanyId,
        allCompanies,
      };

      if (lastRequestRef.current && isEqual(lastRequestRef.current, request)) {
        return;
      }

      setFetching(true);
      setHasError(false);

      try {
        const compositionResponse = await gqlClient.query(
          COMPOSITION_GET_DATA_V3,
          compositionVariables
        );

        const dims = getCompositionBreakdownDims(
          compositionResponse.data?.composition_v3,
          secondaryBreakdown,
          baselineCompanyId,
          MAX_REPORT_ENTITIES
        );

        if (dims.length === 0) {
          lastRequestRef.current = request;
          setDataSet(EMPTY_BREAKDOWN_SINGLE);
          return;
        }

        const baseFilters = stripCompanySentimentFilters(
          sentimentVariables.filters
        );

        const baselineCompany =
          allCompanies.find((c) => c.id === baselineCompanyId) ??
          allCompanies[0] ??
          null;
        if (!baselineCompany) {
          lastRequestRef.current = request;
          setDataSet(EMPTY_BREAKDOWN_SINGLE);
          return;
        }

        const comparisonCompanies = allCompanies;
        if (comparisonCompanies.length === 0) {
          lastRequestRef.current = request;
          setDataSet(EMPTY_BREAKDOWN_SINGLE);
          return;
        }

        const segmentFilters =
          secondaryBreakdown === 'role'
            ? buildRoleSentimentFiltersFromDims(
                dims,
                baseFilters?.custom_role ?? {},
                MAX_REPORT_ENTITIES
              )
            : buildGeographySentimentFiltersFromDims(dims, MAX_REPORT_ENTITIES);

        const dim1 =
          secondaryBreakdown === 'role'
            ? Dimension1.Role
            : Dimension1.Geography;

        const baselineFilters = {
          ...(baseFilters ?? {}),
          ...buildCompanySentimentFilterPatch(baselineCompany),
          ...(segmentFilters as Record<string, unknown>),
        } as NonNullable<GetSentimentDataQueryVariables['filters']>;

        const comparisonFilters = {
          ...(baseFilters ?? {}),
          ...mergeCompanySentimentFilters(comparisonCompanies),
          ...(segmentFilters as Record<string, unknown>),
        } as NonNullable<GetSentimentDataQueryVariables['filters']>;

        const [baselineResponse, comparisonResponse] = await Promise.all([
          gqlClient.query(SENTIMENT_GET_OVERTIME_DATA_V2, {
            dim1,
            filters: baselineFilters,
          }),
          gqlClient.query(SENTIMENT_GET_OVERTIME_DATA_V2, {
            dim1,
            filters: comparisonFilters,
          }),
        ]);

        const toDimMap = (
          response: { data?: GetSentimentDataQuery | null } | null | undefined
        ): Map<number, { value: number; count: number }> => {
          const map = new Map<number, { value: number; count: number }>();
          const entities = (response?.data?.sentiment ?? []).filter(
            (e): e is SentimentEntity => e !== null
          );
          entities.forEach((entity) => {
            const id = entity?.metadata?.id;
            if (id === null || id === undefined) return;
            map.set(id, getSentimentSnapshot(entity, metric));
          });
          return map;
        };

        const baselineDimMap = toDimMap(baselineResponse);
        const comparisonDimMap = toDimMap(comparisonResponse);

        const next: BreakdownSingleDataSet = {
          type: DataType.BREAKDOWN_SINGLE,
          data: [
            {
              dim: baselineCompany.name,
              breakdown: dims.map((dim) => {
                const hit = baselineDimMap.get(dim.id);
                const value = hit?.value ?? 0;
                const count = hit?.count ?? 0;

                return {
                  dim: dim.label,
                  id: String(dim.id),
                  value,
                  share: value,
                  count,
                };
              }),
            },
            {
              dim: `Comparison group (${comparisonCompanies.length})`,
              breakdown: dims.map((dim) => {
                const hit = comparisonDimMap.get(dim.id);
                const value = hit?.value ?? 0;
                const count = hit?.count ?? 0;

                return {
                  dim: dim.label,
                  id: String(dim.id),
                  value,
                  share: value,
                  count,
                };
              }),
            },
          ],
        };

        lastRequestRef.current = request;
        setDataSet(next);
        // biome-ignore lint/correctness/noUnusedVariables: migrated from develop
      } catch (e) {
        setHasError(true);
      } finally {
        setFetching(false);
      }
    })();
  }, [
    allCompanies,
    baselineCompanyId,
    compositionVariables,
    gqlClient,
    metric,
    secondaryBreakdown,
    sentimentVariables.filters,
  ]);

  const status = (() => {
    if (fetching) return DataProviderQueryStatus.Fetching;
    if (hasError) return DataProviderQueryStatus.Error;
    return DataProviderQueryStatus.Success;
  })();

  return children({ data: dataSet, status });
};

export const SentimentDataProvider = ({
  primaryDimension,
  metric,
  chartType,
  isSnapshot = true,
  children,
}: {
  primaryDimension: PrimaryFilterId;
  metric: SentimentMetricValues;
  chartType: SentimentChartType;
  isSnapshot?: boolean;
  children: DataProviderChildren<SentimentData | undefined>;
}) => {
  const dim1 = getDim1(primaryDimension);

  const { primaryFilter } = usePrimaryFilterState(primaryDimension);
  const variables = useGetSentimentVariables({
    primaryDimension,
    isSnapshot,
  });
  const { pageState } = usePageState();
  const { item } = useChart();
  const selectedEntity = pageState.primaryEntities?.[item?.id];
  const secondaryBreakdown = getSecondaryBreakdownDimension(
    item?.plot?.secondaryDimension
  );

  const sentimentQuery = isSnapshot
    ? SENTIMENT_SNAPSHOT
    : SENTIMENT_GET_OVERTIME_DATA_V2;

  if (!dim1) return null;

  if (
    (chartType === 'diverging-bar' || chartType === 'diverging-bar-combo') &&
    metric !== 'sentiment_top_bottom' &&
    (secondaryBreakdown === 'role' || secondaryBreakdown === 'geography')
  ) {
    return (
      <SentimentRoleGeoBreakdownProvider
        metric={metric}
        isSnapshot={isSnapshot}
        secondaryBreakdown={secondaryBreakdown}
        sentimentVariables={variables}
        primaryFilterSelections={primaryFilter?.selections}
      >
        {children}
      </SentimentRoleGeoBreakdownProvider>
    );
  }

  return (
    <SentimentQuery query={sentimentQuery} variables={variables}>
      {({ data, status }) => {
        const chartData = (() => {
          if (!data) return undefined;

          if (metric === 'sentiment_top_bottom') {
            if (chartType === 'diverging-bar-combo') {
              return data?.sentiment
                ? convertSentimentData({
                    data: data.sentiment,
                    metric,
                    chartType,
                    primaryFilterSelections: primaryFilter?.selections,
                  })
                : undefined;
            }

            const selectedEntityData = selectedEntity
              ? data.sentiment?.filter(
                  (entity) => `${entity?.metadata?.id}` === selectedEntity?.id
                )
              : data.sentiment?.slice(0, 1);

            const normalizedData = normalizeBarScoresData(
              { sentiment: selectedEntityData },
              isSnapshot
            );
            const singleEntityData = transformToSingleEntity(normalizedData);

            const singleEntityDataSet: SingleDataSet<{
              count?: number;
            }> = {
              type: DataType.SINGLE,
              data: singleEntityData.data.map((item) => ({
                dim: item.label,
                value: item.value,
                count: item.count ?? undefined,
              })),
            };

            return singleEntityDataSet;
          }

          return data?.sentiment
            ? convertSentimentData({
                data: data.sentiment,
                metric,
                chartType,
                primaryFilterSelections: primaryFilter?.selections,
              })
            : undefined;
        })();

        return children({ data: chartData, status });
      }}
    </SentimentQuery>
  );
};

export const SentimentDataProviderRaw = ({
  primaryDimension,
  isSnapshot = false,
  children,
}: {
  primaryDimension: PrimaryFilterId;
  isSnapshot?: boolean;
  children: DataProviderChildren<GetSentimentDataQuery | undefined>;
}) => {
  const dim1 = getDim1(primaryDimension);
  const variables = useGetSentimentVariables({
    primaryDimension,
    isSnapshot,
  });

  if (!dim1) return null;

  return (
    <SentimentQuery variables={variables}>
      {({ data, status }) => {
        return children({ data, status });
      }}
    </SentimentQuery>
  );
};

const useGetSentimentVariables = ({
  primaryDimension,
  isSnapshot = false,
}: {
  primaryDimension: PrimaryFilterId;
  isSnapshot?: boolean;
}): GetSentimentDataQueryVariables => {
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  const sentimentFilters = getSentimentV2Filters(effectiveFilters, {
    isSnapshot,
  });
  const dim1 = getDim1(primaryDimension);

  return { dim1, filters: sentimentFilters };
};
type SentimentQueryData = GetSentimentDataQuery | GetSentimentSnapshotQuery;

const SentimentQuery = ({
  query = SENTIMENT_GET_OVERTIME_DATA_V2,
  variables,
  children,
}: {
  query?: TypedDocumentNode<SentimentQueryData, GetSentimentDataQueryVariables>;
  variables: GetSentimentDataQueryVariables;
  children: DataProviderChildren<SentimentQueryData | undefined>;
}) => {
  const isTaxonomyResolved = useIsTaxonomyResolved();
  const [{ data, status }] = useUrqlQueryWithStatus<SentimentQueryData>({
    query,
    variables,
    pause: !isTaxonomyResolved,
  });

  return children({ data, status });
};
